import React, { createContext, useContext, useState, useEffect } from 'react';
import { lightTheme, darkTheme } from '../constants/lightTheme';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  // Load theme preference from localStorage (web) or default to dark
  const getInitialTheme = () => {
    if (typeof window !== 'undefined' && window.localStorage) {
      const saved = window.localStorage.getItem('theme');
      return saved === 'light' ? 'light' : 'dark';
    }
    return 'dark';
  };

  const [themeMode, setThemeMode] = useState(getInitialTheme);
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  // Get current theme object
  const theme = themeMode === 'light' ? lightTheme : darkTheme;
  
  // Gradient visibility states - all start as false
  const [gradientStates, setGradientStates] = useState({
    searchInput: false,
    searchButton: false,
    navigationBar: false,
    navigationLogo: false,
    navigationProfile: false,
    mainContent: false
  });

  // Save theme preference
  useEffect(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem('theme', themeMode);
    }
  }, [themeMode]);

  // Toggle between light and dark mode with smooth transition
  const toggleTheme = () => {
    setIsTransitioning(true);
    setThemeMode(prev => prev === 'dark' ? 'light' : 'dark');
    
    // Reset transition state after animation
    setTimeout(() => {
      setIsTransitioning(false);
    }, 300);
  };

  const setTheme = (mode) => {
    if (mode === 'light' || mode === 'dark') {
      setIsTransitioning(true);
      setThemeMode(mode);
      setTimeout(() => setIsTransitioning(false), 300);
    }
  };

  const enableGradient = (key) => {
    setGradientStates(prev => ({
      ...prev,
      [key]: true
    }));
  };

  const disableGradient = (key) => {
    setGradientStates(prev => ({
      ...prev,
      [key]: false
    }));
  };

  const isGradientEnabled = (key) => {
    return gradientStates[key] || false;
  };

  const value = {
    // Theme mode
    themeMode,
    theme,
    isDark: themeMode === 'dark',
    isLight: themeMode === 'light',
    toggleTheme,
    setTheme,
    isTransitioning,
    // Gradients
    gradientStates,
    enableGradient,
    disableGradient,
    isGradientEnabled
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
